<script setup>
// 调整图片尺寸工具（slug resize-image）。复刻 design "Resize Image.html" 内联 <script> 的 resize 逻辑：
// 上传 → canvas 渲染 → 输入 Width/Height（可锁定宽高比）→ 常用预设（%/px/比例尺寸）→ Apply & Download。
// 放大时提示用 AI Upscaler（复刻原 checkUpscale + upscaleHint）。
// 纯浏览器内运算（Canvas 2D / Image / FileReader / URL.createObjectURL），不上传、不依赖任何 npm 包。
import { ref, computed, nextTick, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { useBatch } from "@/composables/useBatch.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";
import { tierOf, fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "resize-image" }, // 单工具，mode 备用
  slug: { type: String, default: "" },
});

const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

const store = useStore();

// 预设（复刻原 #presets 的 data-s / data-w / data-h）
const PRESETS = [
  { label: "50%", s: 0.5 },
  { label: "25%", s: 0.25 },
  { label: "1920w", w: 1920 },
  { label: "1080w", w: 1080 },
  { label: "1:1 1080", w: 1080, h: 1080 },
  { label: "OG 1200×630", w: 1200, h: 630 },
];

const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileName = ref("image.png");
const fileInput = ref(null);
const canvas = ref(null); // 显示用 canvas

const thumbUrl = ref("");
const wIn = ref(0);
const hIn = ref(0);
const locked = ref(true);

// 源图绘制到离屏 work canvas（裁剪/缩放都基于它，复刻原 work canvas）
let work = null;
let baseName = "image";
let srcType = "image/png";
let ratio = 1; // work.width / work.height
let lastUrl = "";

const dimReadout = computed(() => (work ? work.width + " × " + work.height + " px" : "—"));
const showUpscale = computed(() => {
  if (!work) return false;
  const w = parseInt(wIn.value) || 0;
  const h = parseInt(hIn.value) || 0;
  return w > work.width || h > work.height;
});

function fmtType() {
  return srcType === "image/jpeg" ? "image/jpeg" : srcType === "image/webp" ? "image/webp" : "image/png";
}

/* ---------- 批量模式（nuxt#21）：多文件 → 队列，纯客户端 canvas 缩放 ----------
   缩放对每张图独立运算，绝对 Width/Height 没法跨不同尺寸的源图通用，
   所以批量用「预设」(百分比 50%/25% 或固定宽度 1920w/1080w/比例尺寸) —— 一套预设套用所有文件。 */
const batchMode = ref(false);
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const _auth = useAuthModals();
const _paywall = usePaywallModal();

// 批量优先用预设：默认 50% 缩放。用户在 #params 里从 PRESETS 选一个，套用到所有文件。
const batchPresetIdx = ref(0); // 指向 PRESETS 的下标
const batchPreset = computed(() => PRESETS[batchPresetIdx.value] || PRESETS[0]);

const batch = useBatch({
  category: "image",
  client: true,
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileBatch(file),
  buildQuery: () => "",
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

// 按文件缩放：镜像单文件 applyResize+download 逻辑，但输入是任意 file、目标尺寸由当前选中的预设算出。
// 不依赖模块级 work/wIn/hIn state。产物格式跟随源图类型（jpg/webp/png），ext 取目标格式。
async function convertFileBatch(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read image"));
      im.src = url;
    });
    const sw = image.naturalWidth;
    const sh = image.naturalHeight;
    const sr = sw / sh;
    // 预设 → 目标宽高（与 applyPreset 同规则：s=按比例缩放；w/h=固定尺寸，缺 h 时按源宽高比补）。
    const p = batchPreset.value;
    let tw, th;
    if (p.s != null) {
      tw = Math.round(sw * p.s);
      th = Math.round(sh * p.s);
    } else {
      tw = p.w;
      th = p.h != null ? p.h : Math.round(p.w / sr);
    }
    tw = Math.max(1, tw);
    th = Math.max(1, th);
    const c = document.createElement("canvas");
    c.width = tw;
    c.height = th;
    c.getContext("2d").drawImage(image, 0, 0, tw, th);
    // 输出类型跟随源图（jpeg/webp 维持，其余落 png），与单文件 fmtType() 一致。
    const mt = file.type || "";
    const tt = mt === "image/jpeg" ? "image/jpeg" : mt === "image/webp" ? "image/webp" : "image/png";
    const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
    const blob = await new Promise((r) => c.toBlob(r, tt, 0.95));
    if (!blob) throw new Error("Resize failed");
    return { blob, ext };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  batch.addFiles(files);
}
function addMoreFiles() {
  if (fileInput.value) fileInput.value.click();
}
// 队列清空 → 退出批量回到 dropzone；套餐变化 → 重算锁定。
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) batchMode.value = false;
});
watch([tierB], () => batch.reevaluate());
// 切换预设 → 作废已转好的结果（让用户重新 Convert all 拿新尺寸）。
watch(batchPresetIdx, () => {
  batch.items.value.forEach((it) => {
    if (it.status === "done") {
      if (it.outUrl) URL.revokeObjectURL(it.outUrl);
      it.status = "queued";
      it.prog = 0;
      it.outBlob = null;
      it.outUrl = "";
      it.outExt = "";
      it.outSize = 0;
    }
  });
});

function paint() {
  const c = canvas.value;
  if (!c || !work) return;
  c.width = work.width;
  c.height = work.height;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.drawImage(work, 0, 0);
}

function revoke() {
  if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; }
}

async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  // baseName/srcType/fileName 保持取自原始 file(用户的名字/类型)；仅 canvas 源换成可解码的 _f。
  baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  srcType = file.type;
  fileName.value = file.name;
  // 浏览器解不了的源(heic/tiff/tga/dds/psd…)先经后端转 png,用户无感;真解不了(如 ithmb)→ 提示。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  revoke();
  const url = URL.createObjectURL(_f);
  lastUrl = url;
  const im = new Image();
  im.onload = async () => {
    work = document.createElement("canvas");
    work.width = im.naturalWidth;
    work.height = im.naturalHeight;
    work.getContext("2d").drawImage(im, 0, 0);
    ratio = im.naturalWidth / im.naturalHeight;
    wIn.value = im.naturalWidth;
    hIn.value = im.naturalHeight;
    thumbUrl.value = url;
    stage.value = "work";
    await nextTick(); // canvas 在 v-if 里,等挂载后再绘制
    paint();
  };
  im.onerror = () => { alert("Could not load this image."); };
  im.src = url;
}

/* ---- 拖放 / 选择 ---- */
function pick() { fileInput.value && fileInput.value.click(); }
// 统一入口：批量中 → add more；多文件 → 进批量；单文件 → 原有单文件流。
function handleFiles(files) {
  if (!files || !files.length) return;
  if (batchMode.value) { batch.addFiles(files); return; }
  if (files.length > 1) { enterBatch(files); return; }
  loadFile(files[0]);
}
function onInput(e) { handleFiles(e.target.files); e.target.value = ""; }
function onDrop(e) { e.preventDefault(); dragging.value = false; handleFiles(e.dataTransfer && e.dataTransfer.files); }

function reset() {
  stage.value = "drop";
  work = null;
  thumbUrl.value = "";
  if (fileInput.value) fileInput.value.value = "";
}

/* ---- 宽高输入：锁定宽高比时联动（复刻原 wIn/hIn input + lock） ---- */
function toggleLock() { locked.value = !locked.value; }
function onWidthInput() {
  if (locked.value && wIn.value) hIn.value = Math.round(wIn.value / ratio);
}
function onHeightInput() {
  if (locked.value && hIn.value) wIn.value = Math.round(hIn.value * ratio);
}

/* ---- 预设（复刻原 #presets click：data-s 按比例缩放；data-w/data-h 固定尺寸）---- */
function applyPreset(p) {
  if (!work) return;
  if (p.s != null) {
    wIn.value = Math.round(work.width * p.s);
    hIn.value = Math.round(work.height * p.s);
  } else if (p.w != null) {
    wIn.value = p.w;
    hIn.value = p.h != null ? p.h : Math.round(p.w / ratio);
  }
  applyResize();
}

/* ---- 应用缩放：把目标宽高画到新 canvas 并回填为 work（复刻原 applyResize）---- */
function applyResize() {
  if (!work) return;
  const w = Math.max(1, parseInt(wIn.value) || work.width);
  const h = Math.max(1, parseInt(hIn.value) || work.height);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  c.getContext("2d").drawImage(work, 0, 0, w, h);
  work = c;
  ratio = w / h;
  wIn.value = w;
  hIn.value = h;
  paint();
}

/* ---- 下载（复刻原 downloadBtn：若有未应用的尺寸先应用，再 toBlob）---- */
function download() {
  if (!work) return;
  if (parseInt(wIn.value) !== work.width || parseInt(hIn.value) !== work.height) applyResize();
  const tt = fmtType();
  const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
  work.toBlob((b) => downloadBlob(b, baseName + "-resized." + ext), tt, 0.95);
}

onBeforeUnmount(revoke);
</script>

<template>
  <div class="resize-tool">
    <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop' && !batchMode" class="dropzone" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h7v7H3zM14 14h7v7h-7M10 3l11 11" /></svg></div>
      <h3>Drop your image here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
      <div class="dz-or">Resized right in your browser — never stored</div>
      <button class="btn btn-amber dz-btn" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- batch 多文件队列（nuxt#21）：拖入 >1 个文件时进入；共享预设走 #params 插槽 -->
    <div v-if="batchMode" class="workspace show">
      <BatchQueue :batch="batch" category="image" :client="true" :file-count-max="20" @add-more="addMoreFiles">
        <template #params>
          <div class="ctl">
            <label>Resize all to</label>
            <div class="presets">
              <button
                v-for="(p, i) in PRESETS"
                :key="p.label"
                type="button"
                :class="{ active: batchPresetIdx === i }"
                @click="batchPresetIdx = i"
              >{{ p.label }}</button>
            </div>
          </div>
          <div style="font-size: 12.5px; color: var(--tx-faint); font-weight: 600">One set of settings applies to all files — pick a preset once.</div>
        </template>
      </BatchQueue>
    </div>

    <!-- 工作区（单文件） -->
    <div v-if="stage === 'work' && !batchMode" class="workspace show">
      <div class="ws-grid">
        <div class="ws-preview">
          <canvas ref="canvas"></canvas>
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <img class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ dimReadout }}</div>
            </div>
            <span class="rm" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
          </div>

          <!-- 放大提示（复刻原 upscaleHint） -->
          <a v-show="showUpscale" class="upscale-hint" href="https://sharkfoto.com/ai/image/image-upscaler/" target="_blank" rel="noopener">
            <span class="uh-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8V3h5M21 16v5h-5M3 3l7 7M21 21l-7-7" /></svg></span>
            <span class="uh-tx"><b>Enlarging blurs a photo.</b> For sharp HD upscaling that adds real detail, use the AI Image Upscaler →</span>
          </a>

          <!-- 宽高 + 锁定（复刻原 dim-row） -->
          <div class="dim-row">
            <div class="ctl">
              <label>Width</label>
              <div class="field">
                <input type="number" min="1" v-model="wIn" @input="onWidthInput" />
                <span class="unit">px</span>
              </div>
            </div>
            <div class="lk" :class="{ off: !locked }" title="Lock aspect ratio" @click="toggleLock">
              <svg v-if="locked" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 014-4 4 4 0 013.5 2" /></svg>
            </div>
            <div class="ctl">
              <label>Height</label>
              <div class="field">
                <input type="number" min="1" v-model="hIn" @input="onHeightInput" />
                <span class="unit">px</span>
              </div>
            </div>
          </div>

          <!-- 预设（复刻原 #presets） -->
          <div class="presets">
            <button v-for="p in PRESETS" :key="p.label" type="button" @click="applyPreset(p)">{{ p.label }}</button>
          </div>

          <div class="ws-actions">
            <button class="btn btn-amber btn-lg" type="button" @click="download">Apply &amp; Download</button>
            <button class="btn btn-outline" type="button" @click="reset">Resize another image</button>
          </div>
        </div>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live canvas preview</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
    </div>
  </div>
</template>

<style scoped>
.resize-tool { width: 100%; }
.workspace { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; }
.ws-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.ws-preview { position: relative; background: repeating-conic-gradient(#0c0e13 0 25%, #0a0b10 0 50%) 50% / 26px 26px; min-height: 340px; display: grid; place-items: center; padding: 24px; overflow: hidden; }
.ws-preview canvas { max-height: 380px; width: auto; max-width: 100%; border-radius: 8px; box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85); }
.ws-panel { border-left: 1px solid var(--line); padding: 26px; display: flex; flex-direction: column; gap: 18px; }
.ws-file { display: flex; align-items: center; gap: 13px; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
.ws-file .thumb { width: 46px; height: 46px; border-radius: 11px; object-fit: cover; flex: none; background: var(--surface-2); }
.ws-file .meta { min-width: 0; flex: 1; }
.ws-file .fn { font-weight: 700; font-size: 14.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-file .fsz { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.ws-file .rm { color: var(--tx-faint); cursor: pointer; flex: none; padding: 6px; border-radius: 8px; transition: 0.15s; }
.ws-file .rm:hover { color: var(--c-image); background: rgba(255, 255, 255, 0.05); }

/* upscale hint（复刻 design freetools.css .upscale-hint） */
.upscale-hint { display: flex; align-items: center; gap: 11px; padding: 12px 14px; border-radius: 12px; border: 1px solid color-mix(in oklab, var(--brand) 45%, var(--line-strong)); background: color-mix(in oklab, var(--brand) 12%, transparent); color: var(--tx-dim); font-size: 13px; line-height: 1.45; transition: 0.15s; }
.upscale-hint:hover { background: color-mix(in oklab, var(--brand) 20%, transparent); color: var(--tx); }
.upscale-hint .uh-ic { width: 34px; height: 34px; border-radius: 9px; flex: none; display: grid; place-items: center; background: color-mix(in oklab, var(--brand) 22%, transparent); color: #c9b6ff; }
.upscale-hint .uh-ic svg { width: 18px; height: 18px; }
.upscale-hint .uh-tx b { color: #fff; }

/* 宽高行 + 锁定按钮（复刻 design 内联 .dim-row / .lk） */
.dim-row { display: grid; grid-template-columns: 1fr auto 1fr; gap: 10px; align-items: end; }
.dim-row .ctl { min-width: 0; display: flex; flex-direction: column; gap: 9px; }
.dim-row .ctl label { font-size: 13px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.02em; }
.dim-row .field { display: flex; gap: 10px; }
.dim-row .field input[type="number"] { flex: 1; min-width: 0; background: var(--surface-2); border: 1px solid var(--line); border-radius: 10px; padding: 11px 13px; color: var(--tx); font-family: var(--font-b); font-size: 15px; font-weight: 600; outline: none; transition: 0.15s; -moz-appearance: textfield; appearance: textfield; }
.dim-row .field input:focus { border-color: var(--c-free); }
.dim-row .field input::-webkit-inner-spin-button, .dim-row .field input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.dim-row .field .unit { display: grid; place-items: center; padding: 0 13px; border: 1px solid var(--line); border-radius: 10px; color: var(--tx-faint); font-size: 13px; font-weight: 700; background: var(--surface-2); }
.dim-row .lk { align-self: end; width: 41px; height: 41px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface-2); color: var(--c-free); display: grid; place-items: center; cursor: pointer; transition: 0.15s; }
.dim-row .lk.off { color: var(--tx-faint); }
.dim-row .lk svg { width: 17px; height: 17px; }

/* 预设（复刻 design 内联 .presets） */
.presets { display: flex; flex-wrap: wrap; gap: 7px; }
.presets button { padding: 7px 12px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 600; font-size: 12.5px; cursor: pointer; transition: 0.15s; }
.presets button:hover { color: var(--tx); border-color: var(--c-free); }
.presets button.active { color: #1a1205; background: var(--c-free); border-color: var(--c-free); }

.ws-actions { margin-top: auto; display: flex; flex-direction: column; gap: 10px; }
.ws-actions .btn { white-space: normal; text-align: center; line-height: 1.25; }
.trust { display: flex; align-items: center; justify-content: center; gap: 30px; flex-wrap: wrap; margin: 30px auto 0; color: var(--tx-faint); font-size: 13.5px; font-weight: 600; }
.trust .i { display: flex; align-items: center; gap: 8px; }
.trust .i svg { width: 16px; height: 16px; color: var(--c-gif); }
@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } .ws-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
